import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Box,
  Typography,
  Slider,
  TextField,
  Alert,
  IconButton,
  Chip
} from '@mui/material';
import {
  Close as CloseIcon,
  RestartAlt as RestartAltIcon,
  AutoFixHigh as AutoFixHighIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Add as AddIcon,
  Remove as RemoveIcon
} from '@mui/icons-material';

const MatchingCoefficients = ({ initialWeights = null, onWeightsChange }) => {
  const defaultWeights = {
    profile_match: 0.20,
    skills_match: 0.30,
    experience_match: 0.35,
    education_match: 0.15
  };

  const [weights, setWeights] = useState(initialWeights || defaultWeights);
  const [editingChip, setEditingChip] = useState(null);
  const [chipValue, setChipValue] = useState('');

  const labels = {
    profile_match: 'Profil',
    skills_match: 'Compétences',
    experience_match: 'Expérience',
    education_match: 'Éducation'
  };

  const descriptions = {
    profile_match: 'Alignement du titre du poste',
    skills_match: 'Correspondance des compétences techniques',
    experience_match: 'Adéquation de l\'expérience professionnelle',
    education_match: 'Alignement de l\'éducation'
  };

  const total = Object.values(weights).reduce((sum, val) => sum + val, 0);
  const isValid = Math.abs(total - 1.0) < 0.01;

  useEffect(() => {
    if (onWeightsChange) {
      onWeightsChange(weights, isValid);
    }

    // Console log the coefficient state
    const round2 = (num) => num.toFixed(3);

    const coefficientState = {
      "profile_match": round2(weights.profile_match),
      "skills_match": round2(weights.skills_match),
      "experience_match": round2(weights.experience_match * 0.6),
      "experience_years_match": round2(weights.experience_match * 0.4),
      "education_match": round2(weights.education_match)
    };

  }, [weights, onWeightsChange]);

  const handleSliderChange = (key) => (event, newValue) => {
    setWeights(prev => ({
      ...prev,
      [key]: newValue / 100
    }));
  };

  const handleInputChange = (key) => (event) => {
    const value = event.target.value === '' ? '' : Number(event.target.value);
    if (value === '' || (value >= 0 && value <= 100)) {
      setWeights(prev => ({
        ...prev,
        [key]: value === '' ? 0 : value / 100
      }));
    }
  };

  const handleChipClick = (key) => {
    setEditingChip(key);
    setChipValue(Math.round(weights[key] * 100).toString());
  };

  const handleChipChange = (e) => {
    const value = e.target.value;
    if (value === '' || /^\d*$/.test(value)) {
      setChipValue(value);
    }
  };

  const handleChipBlur = () => {
    if (chipValue !== '' && editingChip) {
      const value = Math.min(100, Math.max(0, parseInt(chipValue))) / 100;
      setWeights(prev => ({
        ...prev,
        [editingChip]: value
      }));
    }
    setEditingChip(null);
    setChipValue('');
  };

  const handleChipKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleChipBlur();
    }
  };

  const handleIncrement = (key) => {
    const newValue = Math.min(100, Math.round(weights[key] * 100) + 1) / 100;
    setWeights(prev => ({
      ...prev,
      [key]: newValue
    }));
  };

  const handleDecrement = (key) => {
    const newValue = Math.max(0, Math.round(weights[key] * 100) - 1) / 100;
    setWeights(prev => ({
      ...prev,
      [key]: newValue
    }));
  };

  const resetToDefaults = () => {
    setWeights(defaultWeights);
    if (onWeightsChange) {
      onWeightsChange(defaultWeights, true); // true because default weights sum to 1.0
    }
  };

  const normalizeWeights = () => {
    const sum = Object.values(weights).reduce((acc, val) => acc + val, 0);
    if (sum > 0) {
      const normalized = {};
      Object.keys(weights).forEach(key => {
        normalized[key] = parseFloat((weights[key] / sum).toFixed(2));
      });
      setWeights(normalized);
    }
  };

  return (
    <Box sx={{ p: 0 }}>
      {/* Title */}


      {/* Total Weight Alert */}
      <Alert
        icon={false}
        sx={{
          mb: 2,
          py: 0.5,
          px: 1.5,
          // mx: 1,
          bgcolor: 'transparent',
          border: '1px solid',
          borderRadius: '10px',
          borderColor: isValid ? 'success.main' : 'warning.main'
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
          <Typography variant="body2" sx={{
            fontSize: '0.85rem',
            flexGrow: 1,
            fontFamily: 'Poppins',
            fontWeight: 500,
            color: isValid ? 'success.main' : 'warning.main'
          }}>
            {isValid
              ? 'Les poids sont équilibrés correctement'
              : `Total: ${(total * 100).toFixed(0)}% - Doit être égal à 100%`
            }
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto' }}>
            <IconButton
              size="medium"
              color={isValid ? "primary" : "warning"}
              onClick={normalizeWeights}
              sx={{
                p: 0.75,
                ml: 1,
                minWidth: 'auto',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  transform: 'scale(1.1)',
                  transition: 'all 0.2s ease-in-out'
                }
              }}
            >
              <AutoFixHighIcon sx={{ fontSize: '1.25rem' }} />
            </IconButton>
            <IconButton
              size="medium"
              onClick={resetToDefaults}
              sx={{
                p: 0.75,
                minWidth: 'auto',

              }}
            >
              <RestartAltIcon sx={{ fontSize: '1.25rem' }} />
            </IconButton>
          </Box>
        </Box>
      </Alert>

      {/* Coefficients Grid - 2x2 Layout */}
      <Grid container spacing={1} sx={{ px: 1, pb: 1 }}>
        {Object.keys(weights).map((key) => (
          <Grid item xs={6} key={key}>
            <Box
              sx={{
                p: 1,
                border: '1px solid',
                borderColor: '#e0e0e0',
                borderRadius: '10px',
                bgcolor: 'background.paper',
                transition: '0.3s ease',
                height: '100%',
                minHeight: '60px',
                '&:hover': {
                  borderColor: '#0017E7',
                  boxShadow: '0px 4px 20px rgba(0, 23, 231, 0.1)'
                }
              }}
            >
              {/* Header */}
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.25}>
                <Box>
                  <Typography variant="subtitle2" fontWeight={600} color="text.primary" sx={{ fontSize: '0.75rem', fontFamily: 'Poppins' }}>
                    {labels[key]}
                  </Typography>
                </Box>
                {/* Controls Area */}
                <Box display="flex" alignItems="center">
                  <IconButton
                    size="small"
                    onClick={() => handleDecrement(key)}
                    sx={{ p: 0.25, mr: 0.5 }}
                  >
                    <RemoveIcon fontSize="small" sx={{ fontSize: '1rem' }} />
                  </IconButton>

                  {editingChip === key ? (
                    <TextField
                      value={chipValue}
                      onChange={handleChipChange}
                      onBlur={handleChipBlur}
                      onKeyPress={handleChipKeyPress}
                      autoFocus
                      inputProps={{
                        style: {
                          padding: '2px 4px',
                          fontSize: '0.6rem',
                          fontWeight: 700,
                          width: '30px',
                          height: '16px',
                          textAlign: 'center'
                        }
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            borderColor: 'primary.main',
                            borderWidth: '1px',
                          },
                        },
                      }}
                    />
                  ) : (
                    <Chip
                      label={`${(weights[key] * 100).toFixed(0)}%`}
                      color="primary"
                      size="small"
                      sx={{
                        fontWeight: 700,
                        fontSize: '0.7rem',
                        fontFamily: 'Poppins',
                        minWidth: 35,
                        height: 20,
                        cursor: 'pointer',
                        borderRadius: '8px'
                      }}
                      onClick={() => handleChipClick(key)}
                    />
                  )}

                  <IconButton
                    size="small"
                    onClick={() => handleIncrement(key)}
                    sx={{ p: 0.25, ml: 0.5 }}
                  >
                    <AddIcon fontSize="small" sx={{ fontSize: '1rem' }} />
                  </IconButton>
                </Box>
              </Box>

              {/* Slider */}
              <Slider
                value={weights[key] * 100}
                onChange={handleSliderChange(key)}
                aria-labelledby={`${key}-slider`}
                valueLabelDisplay="auto"
                valueLabelFormat={(value) => `${value}%`}
                min={0}
                max={100}
                step={1}
                sx={{
                  mt: -0.5,
                  mb: -0.5,
                  '& .MuiSlider-thumb': {
                    width: 10,
                    height: 10
                  },
                  '& .MuiSlider-track': {
                    height: 2
                  },
                  '& .MuiSlider-rail': {
                    height: 2
                  }
                }}
              />
            </Box>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};



export default MatchingCoefficients;